import { barHeight, chartRange, daySeries } from '../../core/history'
import { scoreValue } from '../../core/scoring'
import { useT } from '../../platform/i18n/useT'
import { GAMES } from '../../registry'
import { gameTitle } from '../gameText'
import { Reminders } from './Reminders'
import { ScoreBlock } from './ScoreBlock'
import type { Progress } from '../useProgress'
import './screens.css'

/** Прогресс: счёт, статистика по играм и настройки. */
export function ProgressScreen({ progress }: { progress: Progress }) {
  const { t } = useT()

  return (
    <>
      <header className="sc__head">
        <div className="sc__eyebrow">{t('nav.progress')}</div>
        <h1 className="sc__h1">{t('index.title')}</h1>
        <p className="sc__lead">{t('progress.sub')}</p>
      </header>

      <ScoreBlock progress={progress} />

      <div className="sc__label">{t('progress.week')}</div>
      <WeekChart progress={progress} />

      <div className="sc__label">{t('nav.games')}</div>
      {progress.sessionsTotal === 0 ? (
        <p className="sc__empty">{t('progress.noData')}</p>
      ) : (
        progress.games.map((g) => {
          const module = GAMES.find((m) => m.id === g.gameId)
          if (!module) return null
          return (
            <div key={g.gameId} className="sc__game">
              <div className="sc__game-name">{gameTitle(t, module)}</div>
              <div className="sc__game-stats">
                <span className="sc__stat">
                  <b>{scoreValue(g.score) ?? '—'}</b>
                  <small>{t('index.title')}</small>
                </span>
                <span className="sc__stat">
                  <b>{g.sessions}</b>
                  <small>{t('progress.sessionsShort')}</small>
                </span>
                {module.metric === 'span' && (
                  <span className="sc__stat">
                    <b>{g.bestSpan || '—'}</b>
                    <small>{t('progress.bestSpan')}</small>
                  </span>
                )}
              </div>
              {g.score.status === 'collecting' && g.sessions > 0 && (
                <div className="sc__game-hint">
                  {t('progress.collecting', { n: g.score.sessionsLeft })}
                </div>
              )}
            </div>
          )
        })
      )}

      <div className="sc__kv">
        <span>{t('progress.sessions')}</span>
        <span className="sc__kvv">{progress.sessionsTotal}</span>
      </div>

      <Reminders />
    </>
  )
}

/** График за неделю. Пропущенные дни показываем пропуском, а не нулём. */
function WeekChart({ progress }: { progress: Progress }) {
  const { t } = useT()
  const series = daySeries(progress.history)
  const values = series.filter((d) => d?.total != null).map((d) => d?.total as number)

  if (values.length === 0) {
    return <p className="sc__empty">{t('progress.noData')}</p>
  }

  const range = chartRange(values)
  const labels = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
  const todayIdx = new Date().getDay()

  return (
    <div className="sc__chart">
      <div className="sc__bars">
        {series.map((day, i) => (
          <div key={i} className="sc__bar-slot">
            {day?.total != null ? (
              <i
                className={i === series.length - 1 ? 'sc__bar sc__bar--now' : 'sc__bar'}
                style={{ height: `${barHeight(day.total, range)}%` }}
                title={String(day.total)}
              />
            ) : (
              <i className="sc__bar sc__bar--gap" />
            )}
          </div>
        ))}
      </div>
      <div className="sc__days">
        {series.map((_, i) => {
          const offset = (todayIdx - (series.length - 1 - i) + 7) % 7
          return <span key={i}>{t(`day.${labels[offset] ?? 'mon'}`)}</span>
        })}
      </div>
      {progress.weekChange !== null && (
        <div className="sc__delta">
          {t('index.weekChange', {
            n: progress.weekChange > 0 ? `+${progress.weekChange}` : progress.weekChange,
          })}
        </div>
      )}
    </div>
  )
}
